import React, { useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import styled from "styled-components";
import Navbar from "../Navbar";
import Sidebar from "../Sidebar";
import Footer from "../Footer";
import { useSelector } from "react-redux";
import { selectUser } from "../../Redux/currentUserSlice";
import IP_URL from "../connect";

const Main = styled.div``;
const Container = styled.div`
  padding: 2rem;
`;
const Content = styled.div``;
const Section = styled.div`
  margin-top: 20px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;

  th,
  td {
    border: 1px solid #ccc;
    padding: 0.5rem;
    text-align: center;
  }

  th {
    background: #f5f5f5;
  }
`;

const Button = styled.button`
  background: #007bff;
  color: #fff;
  border: none;
  padding: 8px 15px;
  border-radius: 5px;
  cursor: pointer;
  margin: 5px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const AdminAddMeter = () => {
  const [submitting, setSubmitting] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [meterId, setMeterId] = useState("");
  const [meterSize, setMeterSize] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [bulkMeters, setBulkMeters] = useState([]);
  const [mode, setMode] = useState("single"); // single | csv | table

  const { token } = useSelector(selectUser);

  const options = [
    "DN15",
    "DN20",
    "DN25",
    "DN32",
    "DN40",
    "DN50",
    "DN65",
    "DN80",
    "DN100",
    "DN150",
    "DN200",
    "DN300",
  ];

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  // Handle CSV import
  const handleCSVImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const rows = text
        .trim()
        .split("\n")
        .map((row) => row.split(","));
      const meters = rows.map(([meterId, serialNumber, meterSize]) => ({
        meterId,
        serialNumber,
        meterSize,
      }));
      setBulkMeters(meters);
      toast.info(`Imported ${meters.length} meters from CSV`);
    };
    reader.readAsText(file);
  };

  // Add new row for table input
  const addTableRow = () => {
    setBulkMeters([...bulkMeters, { meterId: "", serialNumber: "", meterSize: "" }]);
  };

  // Update table cell
  const updateCell = (index, key, value) => {
    const updated = [...bulkMeters];
    updated[index][key] = value;
    setBulkMeters(updated);
  };

  // Submit single or multiple meters
  const AddMeter = async () => {
    setSubmitting(true);
    try {
      let payload;

      if (mode === "single") {
        if (!meterId || !serialNumber || !meterSize) {
          toast.warn("Please fill all fields.");
          setSubmitting(false);
          return;
        }
        payload = [{ meterId, serialNumber, meterSize }];
      } else {
        if (bulkMeters.length === 0) {
          toast.warn("No meters to upload.");
          setSubmitting(false);
          return;
        }
        payload = bulkMeters;
      }

      const res = await fetch(`${IP_URL}/add-meters`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ meters: payload }),
      });

      const data = await res.json();
      if (!res.ok) toast.error(data.message || "Error adding meters");
      else {
        toast.success(`Added ${payload.length} meter(s) successfully!`);
        setMeterId("");
        setSerialNumber("");
        setMeterSize("");
        setBulkMeters([]);
      }
    } catch (error) {
      toast.error("Error Adding Meter(s)");
      console.error("Error Adding Meter(s)", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Main>
      <Navbar showMenuButton toggleSidebar={toggleSidebar} />
      <Sidebar sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      <Container>
        <h2>Add Meters</h2>
        <div>
          <label>Select Input Mode: </label>
          <select value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="single">Single Meter</option>
            <option value="csv">Import CSV</option>
            <option value="table">Fill Table</option>
          </select>
        </div>

        {mode === "single" && (
          <Section>
            <h4>Single Meter Entry</h4>
            <input
              type="text"
              placeholder="Meter ID"
              value={meterId}
              onChange={(e) => setMeterId(e.target.value)}
            />
            <input
              type="text"
              placeholder="Serial Number"
              value={serialNumber}
              onChange={(e) => setSerialNumber(e.target.value)}
            />
            <select value={meterSize} onChange={(e) => setMeterSize(e.target.value)}>
              <option value="">Select Type</option>
              {options.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </Section>
        )}

        {mode === "csv" && (
          <Section>
            <h4>Import from CSV</h4>
            <p>Format: meterId, serialNumber, meterSize</p>
            <input type="file" accept=".csv" onChange={handleCSVImport} />
            {bulkMeters.length > 0 && (
              <Table>
                <thead>
                  <tr>
                    <th>Meter ID</th>
                    <th>Serial Number</th>
                    <th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {bulkMeters.map((m, i) => (
                    <tr key={i}>
                      <td>{m.meterId}</td>
                      <td>{m.serialNumber}</td>
                      <td>{m.meterSize}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Section>
        )}

        {mode === "table" && (
          <Section>
            <h4>Table Fill Mode</h4>
            <Button onClick={addTableRow}>Add Row</Button>
            {bulkMeters.length > 0 && (
              <Table>
                <thead>
                  <tr>
                    <th>Meter ID</th>
                    <th>Serial Number</th>
                    <th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {bulkMeters.map((m, i) => (
                    <tr key={i}>
                      <td>
                        <input
                          value={m.meterId}
                          onChange={(e) => updateCell(i, "meterId", e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          value={m.serialNumber}
                          onChange={(e) => updateCell(i, "serialNumber", e.target.value)}
                        />
                      </td>
                      <td>
                        <select
                          value={m.meterSize}
                          onChange={(e) => updateCell(i, "meterSize", e.target.value)}
                        >
                          <option value="">Select Type</option>
                          {options.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Section>
        )}

        <Button onClick={AddMeter} disabled={submitting}>
          {submitting ? "Submitting..." : "Submit"}
        </Button>
        <ToastContainer />
      </Container>
      <Footer />
    </Main>
  );
};

export default AdminAddMeter;
